import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  CheckCircle2,
  Clock,
  Eye,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Phone,
  RefreshCcw,
  Save,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import WorkGalleryManager from "./WorkGalleryManager";

const statusOptions = ["New", "Contacted", "Scheduled", "Completed", "Lost"];

function formatDate(value) {
  if (!value) return "Unknown";

  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function getPhotoList(request) {
  return request?.quote_request_photos || [];
}

function formatMetricLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getMetricCount(metrics, key) {
  return metrics?.event_counts?.[key] || 0;
}

export default function AdminDashboard() {
  const [password, setPassword] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [metricsStatus, setMetricsStatus] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoLoadingId, setPhotoLoadingId] = useState(null);

  const functionUrl = process.env.REACT_APP_ADMIN_FUNCTION_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const selectedRequest = useMemo(() => {
    return requests.find((request) => request.id === selectedId) || requests[0] || null;
  }, [requests, selectedId]);

  const stats = useMemo(() => {
    return {
      total: requests.length,
      new: requests.filter((request) => request.status === "New").length,
      scheduled: requests.filter((request) => request.status === "Scheduled").length,
      completed: requests.filter((request) => request.status === "Completed").length,
    };
  }, [requests]);

  async function adminFetch(url, options = {}) {
  if (!functionUrl || !supabaseAnonKey) {
    throw new Error("Missing admin function URL or Supabase anon key in .env.local.");
  }

  const isFormData = options.body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${supabaseAnonKey}`,
    apikey: supabaseAnonKey,
    "x-admin-password": adminPassword,
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Admin request failed.");
  }

  return data;
}
  async function loadRequests() {
    setStatus("loading");
    setMessage("");

    try {
      const data = await adminFetch(`${functionUrl}?action=list`);
      const loadedRequests = data.requests || [];

      setRequests(loadedRequests);
      setSelectedId((currentId) => currentId || loadedRequests[0]?.id || null);
      setStatus("success");
    } catch (error) {
      console.error(error);
      setStatus("error");
      setMessage(error.message);
    }
  }

  async function loadMetrics() {
    setMetricsStatus("loading");

    try {
      const data = await adminFetch(`${functionUrl}?action=metrics`);
      setMetrics(data);
      setMetricsStatus("success");
    } catch (error) {
      console.error(error);
      setMetricsStatus("error");
    }
  }

  async function handleLogin(event) {
    event.preventDefault();

    if (!password.trim()) {
      setMessage("Enter the admin password.");
      return;
    }

    setAdminPassword(password.trim());
    setPassword("");
  }

  useEffect(() => {
    if (adminPassword) {
      loadRequests();
      loadMetrics();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPassword]);

  useEffect(() => {
    function handlePhotoKeyDown(event) {
      if (!photoPreview) return;

      if (event.key === "ArrowRight") {
        movePhoto("next");
      }

      if (event.key === "ArrowLeft") {
        movePhoto("previous");
      }

      if (event.key === "Escape") {
        setPhotoPreview(null);
      }
    }

    window.addEventListener("keydown", handlePhotoKeyDown);
    return () => window.removeEventListener("keydown", handlePhotoKeyDown);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoPreview, selectedRequest]);

  function updateSelectedRequest(updates) {
    if (!selectedRequest) return;

    const updatedRequest = {
      ...selectedRequest,
      ...updates,
    };

    setRequests((current) =>
      current.map((request) => (request.id === selectedRequest.id ? updatedRequest : request))
    );
  }

  async function saveSelectedRequest() {
    if (!selectedRequest) return;

    setSaving(true);
    setMessage("");

    try {
      const data = await adminFetch(`${functionUrl}?action=update`, {
        method: "POST",
        body: JSON.stringify({
          id: selectedRequest.id,
          status: selectedRequest.status,
          admin_notes: selectedRequest.admin_notes || "",
        }),
      });

      setRequests((current) =>
        current.map((request) =>
          request.id === selectedRequest.id
            ? {
                ...request,
                ...data.request,
              }
            : request
        )
      );

      setMessage("Request saved.");
      loadMetrics();
    } catch (error) {
      console.error(error);
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteSelectedRequest() {
    if (!selectedRequest) return;

    const confirmed = window.confirm(
      `Delete quote request from ${selectedRequest.name}? This will also remove attached photos.`
    );

    if (!confirmed) return;

    setDeleting(true);
    setMessage("");

    try {
      await adminFetch(`${functionUrl}?action=delete`, {
        method: "POST",
        body: JSON.stringify({
          id: selectedRequest.id,
        }),
      });

      const remainingRequests = requests.filter(
        (request) => request.id !== selectedRequest.id
      );

      setRequests(remainingRequests);
      setSelectedId(remainingRequests[0]?.id || null);
      setPhotoPreview(null);
      setMessage("Quote request deleted.");
      loadMetrics();
    } catch (error) {
      console.error(error);
      setMessage(error.message);
    } finally {
      setDeleting(false);
    }
  }

  async function openPhoto(photo, index) {
    setPhotoLoadingId(photo.id);
    setMessage("");

    try {
      const data = await adminFetch(`${functionUrl}?action=signed-photo-url`, {
        method: "POST",
        body: JSON.stringify({
          file_path: photo.file_path,
        }),
      });

      setPhotoPreview({
        url: data.signed_url,
        name: photo.file_name,
        index,
      });
    } catch (error) {
      console.error(error);
      setMessage(error.message);
    } finally {
      setPhotoLoadingId(null);
    }
  }

  async function movePhoto(direction) {
    if (!selectedRequest || !photoPreview) return;

    const photos = getPhotoList(selectedRequest);
    if (photos.length <= 1) return;

    const nextIndex =
      direction === "next"
        ? (photoPreview.index + 1) % photos.length
        : (photoPreview.index - 1 + photos.length) % photos.length;

    await openPhoto(photos[nextIndex], nextIndex);
  }

  function refreshDashboard() {
    loadRequests();
    loadMetrics();
  }

  function logout() {
    setAdminPassword("");
    setPassword("");
    setRequests([]);
    setSelectedId(null);
    setStatus("idle");
    setMessage("");
    setMetrics(null);
    setMetricsStatus("idle");
    setPhotoPreview(null);
  }

  if (!adminPassword) {
    return (
      <div className="min-h-screen bg-[#050805] px-5 py-10 text-white">
        <div className="mx-auto grid min-h-[80vh] max-w-md place-items-center">
          <form
            onSubmit={handleLogin}
            className="w-full rounded-[2rem] border border-white/10 bg-white/[0.06] p-6 shadow-2xl shadow-black/30 backdrop-blur"
          >
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-lime-400 text-black">
              <Lock className="h-7 w-7" />
            </div>

            <h1 className="mt-6 text-3xl font-black tracking-tight">
              Admin Dashboard
            </h1>

            <p className="mt-3 leading-7 text-slate-300">
              Enter the admin password to view quote requests for Precision Lawn Workx LLC.
            </p>

            <label className="mt-6 grid gap-2 text-sm font-black">
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="rounded-2xl border border-white/10 bg-black/40 px-4 py-4 font-semibold text-white outline-none transition focus:border-lime-300"
                placeholder="Enter admin password"
              />
            </label>

            <button
              type="submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-lime-400 px-6 py-4 font-black text-black transition hover:bg-lime-300"
            >
              Open Dashboard <ArrowRight className="h-5 w-5" />
            </button>

            {message && (
              <p className="mt-4 rounded-2xl bg-red-500/10 p-4 text-sm font-bold text-red-200 ring-1 ring-red-400/20">
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f8f2] text-slate-950">
      <header className="sticky top-0 z-40 border-b border-black/10 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 lg:px-8">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-green-700">
              Precision Lawn Workx LLC
            </p>
            <h1 className="text-2xl font-black tracking-tight">Quote Dashboard</h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={refreshDashboard}
              className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm font-black transition hover:bg-lime-50"
            >
              <RefreshCcw className="h-4 w-4" />
              Refresh
            </button>

            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-3 text-sm font-black text-white transition hover:bg-green-800"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Total</p>
            <p className="mt-2 text-3xl font-black">{stats.total}</p>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">New</p>
            <p className="mt-2 text-3xl font-black text-green-700">{stats.new}</p>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
              Scheduled
            </p>
            <p className="mt-2 text-3xl font-black">{stats.scheduled}</p>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
              Completed
            </p>
            <p className="mt-2 text-3xl font-black">{stats.completed}</p>
          </div>
        </div>

        <section className="mt-6 rounded-[2rem] bg-white p-5 shadow-sm ring-1 ring-black/5">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.24em] text-green-700">
                Site Metrics
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-tight">
                Last {metrics?.range_days || 30} Days
              </h2>
            </div>

            <p className="text-sm font-bold text-slate-500">
              {metricsStatus === "loading"
                ? "Loading metrics..."
                : metricsStatus === "error"
                  ? "Metrics failed to load."
                  : "Tracked from website activity."}
            </p>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
                Page Views
              </p>
              <p className="mt-2 text-3xl font-black">
                {getMetricCount(metrics, "page_view")}
              </p>
            </div>

            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
                Quote Submits
              </p>
              <p className="mt-2 text-3xl font-black text-green-700">
                {getMetricCount(metrics, "quote_submit")}
              </p>
            </div>

            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
                Call Clicks
              </p>
              <p className="mt-2 text-3xl font-black">
                {getMetricCount(metrics, "call_click")}
              </p>
            </div>

            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
                Email Clicks
              </p>
              <p className="mt-2 text-3xl font-black">
                {getMetricCount(metrics, "email_click")}
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
              <h3 className="font-black">Most Requested Services</h3>

              <div className="mt-4 grid gap-3">
                {Object.entries(metrics?.service_counts || {}).length > 0 ? (
                  Object.entries(metrics.service_counts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([service, count]) => (
                      <div key={service} className="flex items-center justify-between gap-3">
                        <span className="text-sm font-bold text-slate-700">{service}</span>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-green-700 ring-1 ring-black/5">
                          {count}
                        </span>
                      </div>
                    ))
                ) : (
                  <p className="text-sm font-semibold text-slate-500">
                    No service request data yet.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
              <h3 className="font-black">Recent Activity</h3>

              <div className="mt-4 grid gap-3">
                {metrics?.recent_events?.length > 0 ? (
                  metrics.recent_events.slice(0, 6).map((event) => (
                    <div
                      key={event.id}
                      className="rounded-xl bg-white p-3 text-sm ring-1 ring-black/5"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-black">
                          {formatMetricLabel(event.event_type)}
                        </span>
                        <span className="text-xs font-bold text-slate-400">
                          {formatDate(event.created_at)}
                        </span>
                      </div>

                      <p className="mt-1 truncate text-xs font-semibold text-slate-500">
                        {event.page || "Unknown page"}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm font-semibold text-slate-500">
                    No recent activity yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
        <WorkGalleryManager adminFetch={adminFetch} functionUrl={functionUrl} />
        {message && (
          <div className="mt-5 rounded-2xl bg-white p-4 text-sm font-bold text-slate-700 ring-1 ring-black/5">
            {message}
          </div>
        )}

        {status === "loading" && (
          <div className="mt-8 flex items-center gap-3 rounded-2xl bg-white p-5 font-black ring-1 ring-black/5">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading quote requests...
          </div>
        )}

        {status === "error" && (
          <div className="mt-8 rounded-2xl bg-red-50 p-5 font-bold text-red-700 ring-1 ring-red-200">
            {message}
          </div>
        )}

        {status === "success" && requests.length === 0 && (
          <div className="mt-8 rounded-2xl bg-white p-8 text-center ring-1 ring-black/5">
            <h2 className="text-2xl font-black">No quote requests yet.</h2>
            <p className="mt-2 text-slate-600">Submitted quote requests will appear here.</p>
          </div>
        )}

        {requests.length > 0 && (
          <div className="mt-8 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <section className="rounded-[2rem] bg-white p-3 shadow-sm ring-1 ring-black/5">
              <div className="p-3">
                <h2 className="text-xl font-black">Requests</h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">
                  Newest requests appear first.
                </p>
              </div>

              <div className="grid gap-2">
                {requests.map((request) => {
                  const active = selectedRequest?.id === request.id;

                  return (
                    <button
                      key={request.id}
                      onClick={() => setSelectedId(request.id)}
                      className={`rounded-2xl p-4 text-left transition ${
                        active ? "bg-lime-50 ring-1 ring-lime-300" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-black">{request.name}</h3>
                          <p className="mt-1 text-sm font-semibold text-slate-500">
                            {request.service}
                          </p>
                        </div>

                        <span
                          className={`rounded-full px-3 py-1 text-xs font-black ${
                            request.status === "New"
                              ? "bg-green-100 text-green-800"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {request.status}
                        </span>
                      </div>

                      <div className="mt-3 flex items-center gap-2 text-xs font-bold text-slate-400">
                        <Clock className="h-3.5 w-3.5" />
                        {formatDate(request.created_at)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {selectedRequest && (
              <section className="rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-black/5">
                <div className="flex flex-col justify-between gap-4 border-b border-black/10 pb-6 sm:flex-row sm:items-start">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-green-700">
                      Quote Request
                    </p>
                    <h2 className="mt-2 text-3xl font-black tracking-tight">
                      {selectedRequest.name}
                    </h2>
                    <p className="mt-2 text-sm font-bold text-slate-500">
                      Submitted {formatDate(selectedRequest.created_at)}
                    </p>
                  </div>

                  <select
                    value={selectedRequest.status}
                    onChange={(event) =>
                      updateSelectedRequest({
                        status: event.target.value,
                      })
                    }
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-black outline-none focus:border-green-500"
                  >
                    {statusOptions.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-black/5">
                    <Phone className="h-5 w-5 text-green-700" />
                    <p className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                      Phone
                    </p>
                    <a href={`tel:${selectedRequest.phone}`} className="mt-1 block font-black">
                      {selectedRequest.phone}
                    </a>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-black/5">
                    <Mail className="h-5 w-5 text-green-700" />
                    <p className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                      Email
                    </p>
                    <a
                      href={`mailto:${selectedRequest.email || ""}`}
                      className="mt-1 block break-all font-black"
                    >
                      {selectedRequest.email || "Not provided"}
                    </a>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-black/5">
                    <MapPin className="h-5 w-5 text-green-700" />
                    <p className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                      Location
                    </p>
                    <p className="mt-1 font-black">{selectedRequest.location || "Not provided"}</p>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-black/5">
                    <CheckCircle2 className="h-5 w-5 text-green-700" />
                    <p className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                      Preferred Contact
                    </p>
                    <p className="mt-1 font-black">
                      {selectedRequest.preferred_contact || "No preference"}
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Service
                  </p>
                  <p className="mt-2 text-xl font-black">{selectedRequest.service}</p>

                  <p className="mt-5 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
                    Details
                  </p>
                  <p className="mt-2 whitespace-pre-wrap leading-7 text-slate-700">
                    {selectedRequest.details}
                  </p>
                </div>

                <div className="mt-6 rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
                  <div className="flex items-center gap-2">
                    <Camera className="h-5 w-5 text-green-700" />
                    <h3 className="font-black">Attached Photos</h3>
                  </div>

                  {selectedRequest.quote_request_photos?.length > 0 ? (
                    <div className="mt-4 grid gap-3">
                      {selectedRequest.quote_request_photos.map((photo, index) => (
                        <div
                          key={photo.id}
                          className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 ring-1 ring-black/5"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-bold">{photo.file_name}</p>
                            <p className="text-xs font-semibold text-slate-400">
                              Photo {index + 1} of {selectedRequest.quote_request_photos.length} •{" "}
                              {photo.file_type} • {formatFileSize(photo.file_size)}
                            </p>
                          </div>

                          <button
                            onClick={() => openPhoto(photo, index)}
                            className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-black px-3 py-2 text-xs font-black text-white hover:bg-green-800"
                          >
                            {photoLoadingId === photo.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                            {photoLoadingId === photo.id ? "Loading" : "View"}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm font-semibold text-slate-500">
                      No photos attached.
                    </p>
                  )}
                </div>

                <div className="mt-6 rounded-2xl bg-slate-50 p-5 ring-1 ring-black/5">
                  <div className="flex items-center gap-2">
                    <StickyNote className="h-5 w-5 text-green-700" />
                    <h3 className="font-black">Admin Notes</h3>
                  </div>

                  <textarea
                    value={selectedRequest.admin_notes || ""}
                    onChange={(event) =>
                      updateSelectedRequest({
                        admin_notes: event.target.value,
                      })
                    }
                    rows="5"
                    className="mt-4 w-full resize-none rounded-2xl border border-slate-200 bg-white p-4 font-semibold outline-none focus:border-green-500"
                    placeholder="Add internal notes here..."
                  />

                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <button
                      onClick={saveSelectedRequest}
                      disabled={saving}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-green-700 px-5 py-3 font-black text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {saving ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Save className="h-5 w-5" />
                      )}
                      Save Request
                    </button>

                    <button
                      onClick={deleteSelectedRequest}
                      disabled={deleting}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-700 px-5 py-3 font-black text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {deleting ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Trash2 className="h-5 w-5" />
                      )}
                      Delete Request
                    </button>
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {photoPreview && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 px-4 py-6 backdrop-blur-sm">
          <div className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b border-black/10 px-5 py-4">
              <div className="min-w-0">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-green-700">
                  Attached Photo
                </p>

                <h3 className="truncate text-lg font-black">{photoPreview.name}</h3>

                {selectedRequest?.quote_request_photos?.length > 1 && (
                  <p className="mt-1 text-xs font-bold text-slate-500">
                    Photo {photoPreview.index + 1} of {selectedRequest.quote_request_photos.length}
                  </p>
                )}
              </div>

              <button
                onClick={() => setPhotoPreview(null)}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-black text-white transition hover:bg-green-800"
                aria-label="Close photo preview"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="relative max-h-[78vh] overflow-auto bg-slate-950 p-4">
              {selectedRequest?.quote_request_photos?.length > 1 && (
                <>
                  <button
                    onClick={() => movePhoto("previous")}
                    className="absolute left-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-black shadow-xl transition hover:bg-lime-300"
                    aria-label="Previous photo"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>

                  <button
                    onClick={() => movePhoto("next")}
                    className="absolute right-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-black shadow-xl transition hover:bg-lime-300"
                    aria-label="Next photo"
                  >
                    <ArrowRight className="h-6 w-6" />
                  </button>
                </>
              )}

              {photoLoadingId ? (
                <div className="grid min-h-[50vh] place-items-center text-white">
                  <div className="flex items-center gap-3 font-black">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    Loading photo...
                  </div>
                </div>
              ) : (
                <img
                  src={photoPreview.url}
                  alt={photoPreview.name}
                  className="mx-auto max-h-[74vh] w-auto max-w-full rounded-xl object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}